import httpClient from 'shared/lib/httpClient';
import {
  checkTokenAuth,
  clearLocalStorageData,
  checkMenus,
  redirectIfAccessRevoked,
  _invalidateMenusCache,
} from 'shared/lib/authUtils';
import { refreshToken } from 'app/providers/refreshToken';
import { getErrorMessage } from 'shared/utils';
import { toast } from 'react-toastify';

// Request interceptor — attach auth token or shared token per request
httpClient.interceptors.request.use(
  (config) => {
    const isWebView = localStorage.getItem('isWebView') === 'true';
    const sharedToken = localStorage.getItem('sharedToken');

    if (isWebView && sharedToken) {
      config.url = `/shares${config.url.charAt(0) === '/' ? config.url : '/' + config.url}`;
      config.headers['Shared-Token'] = sharedToken;
      return config;
    }

    const token = checkTokenAuth();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor — handle token expiry, refresh, and 401 redirect
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

// Cross-tab refresh lock — prevents multiple tabs from refreshing simultaneously.
// Uses localStorage as a shared mutex; storage events signal other tabs.
const CROSS_TAB_REFRESH_LOCK_KEY = '__token_refreshing__';
const CROSS_TAB_LOCK_TTL_MS = 15000;

// Logout broadcast — signals all other tabs to redirect to /login.
// Must be written BEFORE clearLocalStorageData() so other tabs receive the storage event.
const LOGOUT_SIGNAL_KEY = '__logout_signal__';

const broadcastLogout = () => {
  localStorage.setItem(LOGOUT_SIGNAL_KEY, Date.now().toString());
};

const isAnotherTabRefreshing = () => {
  // console.log('from isAnotherTabRefreshing');
  try {
    const raw = localStorage.getItem(CROSS_TAB_REFRESH_LOCK_KEY);
    if (!raw) return false;
    const { ts } = JSON.parse(raw);
    return Date.now() - ts < CROSS_TAB_LOCK_TTL_MS;
  } catch {
    return false;
  }
};

const acquireCrossTabLock = () => {
  localStorage.setItem(
    CROSS_TAB_REFRESH_LOCK_KEY,
    JSON.stringify({ ts: Date.now() })
  );
};

const releaseCrossTabLock = () => {
  localStorage.removeItem(CROSS_TAB_REFRESH_LOCK_KEY);
};

// Redirect this tab when another tab broadcasts a logout.
// storage events only fire in OTHER tabs, so this covers all idle tabs.
window.addEventListener('storage', (event) => {
  if (event.key === LOGOUT_SIGNAL_KEY && event.newValue) {
    clearLocalStorageData();
    window.location.href = '/login';
    return;
  }

  // Another tab refreshed permissions (wrote the `menus` key). Drop this tab's
  // memoized menus so guards re-read fresh data, then redirect if this tab lost
  // access to the route it is currently on.
  if (event.key === 'menus' && event.newValue) {
    _invalidateMenusCache();
    redirectIfAccessRevoked(checkMenus() ?? []);
  }
});

// Wait for another tab to finish refreshing by listening to localStorage storage events.
// Resolves with the new token when the other tab writes it, or rejects on timeout/failure.
const waitForCrossTabRefresh = () => {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      cleanup();
      reject(new Error('Cross-tab refresh timeout'));
    }, CROSS_TAB_LOCK_TTL_MS);

    const cleanup = () => {
      clearTimeout(timer);
      window.removeEventListener('storage', onStorage);
    };

    const onStorage = (event) => {
      // Another tab saved a new token → read it and resolve
      if (event.key === 'token' && event.newValue) {
        cleanup();
        // console.log('refresh token tab');
        resolve(checkTokenAuth());
        return;
      }
      // Lock was removed without a new token → the other tab's refresh failed
      if (event.key === CROSS_TAB_REFRESH_LOCK_KEY && event.newValue === null) {
        cleanup();
        reject(new Error('Token refresh failed in another tab'));
      }
    };

    window.addEventListener('storage', onStorage);
  });
};

httpClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // SA-1700: the logout request is best-effort and self-cleans in
    // logoutSession(). Skip refresh/forced-redirect handling so a 401 or
    // expired token on /logout doesn't trigger a second teardown or replay.
    const isLogoutRequest = originalRequest?.url?.includes('/logout');
    if (isLogoutRequest) return Promise.reject(error);

    if (error.response?.data?.errors[0]?.message === 'Shared Token Expired') {
      clearLocalStorageData();
      window.location.href = '/expired-link';
    }

    if (error.response) {
      if (error.response?.data?.errors[0]?.message === 'Token Expired') {
        // Within the same tab: queue the request behind the in-progress refresh
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return httpClient(originalRequest);
            })
            .catch((err) => Promise.reject(err));
        }

        // Cross-tab: if another tab already refreshed, the token in localStorage
        // will differ from the one that was sent with the failed request.
        // Retry directly without calling refresh again.
        const failedToken = originalRequest.headers.Authorization?.replace(
          'Bearer ',
          ''
        );
        const currentToken = checkTokenAuth();
        if (currentToken && currentToken !== failedToken) {
          originalRequest.headers.Authorization = `Bearer ${currentToken}`;
          return httpClient(originalRequest);
        }

        // Cross-tab: another tab is currently in the middle of refreshing.
        // Wait for it to finish instead of sending a second refresh request.
        if (isAnotherTabRefreshing()) {
          try {
            const newToken = await waitForCrossTabRefresh();
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return httpClient(originalRequest);
          } catch (err) {
            clearLocalStorageData();
            window.location.href = '/login';
            return Promise.reject(err);
          }
        }

        originalRequest._retry = true;
        isRefreshing = true;
        acquireCrossTabLock();

        try {
          const newToken = await refreshToken();
          processQueue(null, newToken);
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return httpClient(originalRequest);
        } catch (err) {
          // Before giving up, check if another tab refreshed successfully while we were failing.
          // This handles the TOCTOU race where two tabs both attempt refresh simultaneously.
          const latestToken = checkTokenAuth();
          if (latestToken && latestToken !== failedToken) {
            processQueue(null, latestToken);
            originalRequest.headers.Authorization = `Bearer ${latestToken}`;
            return httpClient(originalRequest);
          }

          // Another tab may still be in-flight — wait for its storage event instead of sleeping.
          if (isAnotherTabRefreshing()) {
            try {
              const newToken = await waitForCrossTabRefresh();
              processQueue(null, newToken);
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              return httpClient(originalRequest);
            } catch {
              // waitForCrossTabRefresh timed out or the other tab also failed — fall through to logout
            }
          }

          console.error('Refresh token failed:', err);
          const message = getErrorMessage(err);
          toast.error(`Refresh token: ${message}`);
          processQueue(err, null);
          broadcastLogout();
          clearLocalStorageData();
          window.location.href = '/login';
          return Promise.reject(err);
        } finally {
          isRefreshing = false;
          releaseCrossTabLock();
        }
      }

      if (error.response?.data?.errors[0]?.message === 'Unauthorized') {
        clearLocalStorageData();
        window.location.href = '/login';
      }

      if (error.response.status === 401) {
        clearLocalStorageData();
        window.location.href = '/login';
      }
    } else if (error.request) {
      console.error('No response received:', error.request);
    } else {
      console.error('Error in request setup:', error?.message);
    }

    return Promise.reject(error);
  }
);
